/// <amd-module name="Wit/Common/BacklogContent/AgileFilterBar" />
import * as _ from "BacklogContent/Scripts/libs/lodash";
import * as React from "react";
import { TfsComponent } from "Tfs/Platform/TfsComponent";
import { IObservableValue } from "VSS/Core/Observable";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssComponentContext } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { FILTER_CHANGE_EVENT, IFilter, IFilterState } from "VSSUI/Utilities/Filter";
import * as Resources from "Wit/Common/BacklogContent/Resources";
import { BacklogColumnSettings } from "Wit/Common/BacklogContentService/BacklogContentContants";
import { HubType } from "Wit/Common/BacklogContentService/BacklogContentTreeTypes";
import { IBacklogWorkItemService } from "Wit/Common/BacklogContentService/IBacklogWorkItemService";
import { ICommonBacklogContentService } from "Wit/Common/BacklogContentService/ICommonBacklogContentService";
import { IHubContentService } from "Wit/Common/BacklogContentService/IHubContentService";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { ITeamSettingsService } from "Wit/Common/TeamServices/TeamSettingsService";
import { AreaPathFilterProvider } from "Wit/Filter/AreaPathFilterProvider";
import { AssignedToFilterProvider } from "Wit/Filter/AssignedToFilterProvider";
import { FieldFilterProvider } from "Wit/Filter/FieldFilterProvider";
import { FilterManager, FilterState, isFilterStateEmpty, mapFilterStateToHistoryState, mapHistoryStateToFilterState } from "Wit/Filter/FilterManager";
import {
    AssignedToFilterValueProvider,
    IterationPathFilterValueProvider,
    ParentItemFilterValueProvider,
    StateFilterValueProvider,
    WorkItemTypeFilterValueProvider
} from "Wit/Filter/FilterValueProviders";
import { IterationPathFilterProvider } from "Wit/Filter/IterationPathFilterProvider";
import { ParentFilterProvider } from "Wit/Filter/ParentFilterProvider";
import { TagsFilterProvider } from "Wit/Filter/TagsFilterProvider";
import { TextFilterProvider, TextFilterProviderWithUnassigned } from "Wit/Filter/TextFilterProvider";
import {
    DataSourceFilterValueProvider,
    IWorkItemFilterField,
    mapFromFilterState,
    mapToFilterState,
    resolveFilterState,
    WorkItemFilter,
    WorkItemFilterFieldType
} from "Wit/Filter/WorkItemFilter";

export interface IAgileFilterProps {
    /**
     * Class name to be applied to filter bar
     */
    className?: string;

    /*
     * The view data source
     */
    dataSource: ICommonBacklogContentService;

    /*
     * The VSS filter to subscribe to, if any
     */
    filter: IFilter;

    /*
     * If the filter is visible
     */
    filterToggled: IObservableValue<boolean>;

    /*
     * The current filter manager
     */
    filterManager: FilterManager;

    /**
     * Hub that this filter is used on - sprints or backlogs
     */
    hub: HubType;

    /**
     * Content service to manage saving the filter state
     */
    hubService: IHubContentService;

    /**
     * Keep hierarchy with filters
     */
    keepHierarchyWithFilters?: IObservableValue<boolean>;
}


export class AgileFilterBar extends TfsComponent<IAgileFilterProps> {
    private filterFields: IWorkItemFilterField[] = [];
    private filterThrottleWait: number = 600;
    private showLoadingSpinnerOnFilterChange: boolean;

    constructor(props: IAgileFilterProps, context: IVssComponentContext) {
        super(props, context);
        const { dataSource, filter } = this.props;

        this.showLoadingSpinnerOnFilterChange = isFeatureFlagEnabled(context.pageContext, FeatureFlags.LoadingSpinnerOnFilterChangeEnabled, false);

        dataSource.getBacklogTree();

        const projectId = this.projectData?.id ?? "";
        this.filterFields = [
            {
                displayType: WorkItemFilterFieldType.Text,
                fieldName: TextFilterProvider.PROVIDER_TYPE,
                placeholder: Resources.FilterByKeyword
            },
            {
                displayType: WorkItemFilterFieldType.CheckboxList,
                fieldName: CoreFieldRefNames.WorkItemType,
                placeholder: Resources.FilterByTypes,
                noItemsText: Resources.FilterNoTypes,
                valueProvider: new WorkItemTypeFilterValueProvider(projectId, dataSource /* WorkItemTypeColorAndIconMap */)
            },
            {
                displayType: WorkItemFilterFieldType.CheckboxList,
                fieldName: CoreFieldRefNames.AssignedTo,
                placeholder: Resources.FilterByAssignedTo,
                valueProvider: new AssignedToFilterValueProvider(this.context.pageContext, dataSource)
            },
            {
                displayType: WorkItemFilterFieldType.CheckboxList,
                fieldName: CoreFieldRefNames.State,
                placeholder: Resources.FilterByStates,
                noItemsText: Resources.FilterNoStates,
                valueProvider: new StateFilterValueProvider(projectId, dataSource)
            },
            {
                displayType: WorkItemFilterFieldType.CheckboxList,
                fieldName: CoreFieldRefNames.AreaPath,
                placeholder: Resources.FilterByAreaPath,
                noItemsText: Resources.FilterNoArea,
                valueProvider: new DataSourceFilterValueProvider(CoreFieldRefNames.AreaPath, dataSource)
            }
        ];

        if (props.hub === HubType.Backlogs) {
            this.filterFields.push({
                displayType: WorkItemFilterFieldType.CheckboxList,
                fieldName: CoreFieldRefNames.IterationPath,
                placeholder: Resources.FilterByIteration,
                noItemsText: Resources.FilterNoIterations,
                valueProvider: new IterationPathFilterValueProvider(this.context.pageContext, projectId, dataSource, true)
            });
        }

        this.filterFields.push({
            displayType: WorkItemFilterFieldType.CheckboxList,
            fieldName: CoreFieldRefNames.Tags,
            placeholder: Resources.FilterByTags,
            noItemsText: Resources.FilterNoTags,
            valueProvider: new DataSourceFilterValueProvider(CoreFieldRefNames.Tags, dataSource),
            showOrAndOperators: true
        });

        if (props.hub === HubType.Sprints) {
            this.filterFields.push({
                displayType: WorkItemFilterFieldType.CheckboxList,
                fieldName: CoreFieldRefNames.Parent,
                placeholder: Resources.FilterByParent,
                noItemsText: Resources.FilterNoParents,
                valueProvider: new ParentItemFilterValueProvider(this.context.pageContext, dataSource)
            });
        }
    }

    public async componentDidMount(): Promise<void> {
        const { dataSource, filter, filterManager, hub, filterToggled } = this.props;

        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const pageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData = pageService.getData();

        filterManager.activate(this.context.pageContext);
        filter.subscribe(this._onChangeEvent, FILTER_CHANGE_EVENT);
        filterManager.clearFilterProviders();
        filterManager.registerFilterProvider(TextFilterProvider.PROVIDER_TYPE, new TextFilterProviderWithUnassigned());
        filterManager.registerFilterProvider(CoreFieldRefNames.WorkItemType, new FieldFilterProvider(CoreFieldRefNames.WorkItemType));
        filterManager.registerFilterProvider(CoreFieldRefNames.State, new FieldFilterProvider(CoreFieldRefNames.State));
        filterManager.registerFilterProvider(CoreFieldRefNames.AreaPath, new AreaPathFilterProvider());
        filterManager.registerFilterProvider(CoreFieldRefNames.Tags, new TagsFilterProvider());
        if (hub === HubType.Backlogs) {
            const teamSettingsService = this.context.pageContext.getService<ITeamSettingsService>("ITeamSettingsService");
            const teamSettings = await teamSettingsService.getTeamSettings();
            filterManager.registerFilterProvider(
                CoreFieldRefNames.IterationPath,
                new IterationPathFilterProvider(teamSettings?.currentIteration?.friendlyPath)
            );
        } else {
            filterManager.registerFilterProvider(CoreFieldRefNames.Parent, new ParentFilterProvider());
        }
        pageData && filterManager.registerFilterProvider(CoreFieldRefNames.AssignedTo, new AssignedToFilterProvider(pageData.user, this.context.pageContext));

        let filterState: IFilterState = filter.getState();
        // If filter is already intitialized, don't get the initialize the state again
        if (Object.keys(filterState).length) {
            this.updateFilterState(filterState, historyService);
            filterState = await resolveFilterState(this.filterFields, dataSource, mapToFilterState(filterState));
        } else {
            const initialFilterState = mapHistoryStateToFilterState(
                historyService.getState(),
                this.filterFields.map(f => f.fieldName)
            );

            filterState = await resolveFilterState(this.filterFields, dataSource, initialFilterState);
        }
        filter.setState(filterState);
    }

    public async componentWillUnmount(): Promise<void> {
        this.props.filterManager.clearFilterProviders();
        this.props.filterManager.deactivate();
        this.props.filter.unsubscribe(this._onChangeEvent, FILTER_CHANGE_EVENT);
    }

    public render(): JSX.Element {
        const { className, dataSource, filter, filterToggled } = this.props;
        return (
            <ConditionalChildren renderChildren={filterToggled}>
                <div className={className}>
                    <WorkItemFilter
                        dataSource={dataSource}
                        fields={this.filterFields}
                        filter={filter}
                        filterUpdatedCallback={this.showLoadingSpinnerOnFilterChange ? undefined : this._onFilterChanged}
                        pageContext={this.context.pageContext}
                        setDefaultFilter={false}
                        throttleWait={this.props.keepHierarchyWithFilters?.value === true ? this.filterThrottleWait : undefined}
                    />
                </div>
            </ConditionalChildren>
        );
    }

    private _onFilterChanged = (filterState: FilterState, action?: string) => {
        const { filterManager, dataSource } = this.props;

        if (!dataSource.getBacklogTree().value) {
            return;
        }

        const allRows = dataSource.getBacklogTree().value!.allRows;

        // Remove empty filters from the state object
        for (const key of Object.keys(filterState)) {
            if (filterState[key]?.values.length === 0 || !filterState[key].values[0]) {
                delete filterState[key];
            }
        }

        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const historyState = historyService.getState();
        const newHistoryState = mapFilterStateToHistoryState(filterState);
        for (const key of Object.keys(newHistoryState)) {
            historyState[key] = newHistoryState[key];
        }

        // remove any entries that got cleared
        this.filterFields.forEach(field => !newHistoryState[field.fieldName] && delete historyState[field.fieldName]);

        historyService.replaceState(historyState);

        if (action === "save-to-server") {
            const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
            const key = this.props.hubService.getFilterKey();
            const JSONFilter = JSON.stringify(mapFromFilterState(filterState));
            settingsService.setEntries({ [key]: JSONFilter }, SettingsUserScope.Me, BacklogColumnSettings.TeamScope, dataSource.getCurrentTeam().id);
        }

        filterManager.setFilters(filterState);

        let filteredIds = [];
        let flatFilter = true;
        if (isFilterStateEmpty(filterState)) {
            // If the filter is empty we need to revert back to the original backlog tree
            flatFilter = false;
            filteredIds = dataSource.getBacklogTree().value!.rows.map(row => row.data.id);
            dataSource.getShowFilterHeader().value = false;
        } else {
            this.props.filterToggled.value = true;
            filteredIds = filterManager.filter(this.context.pageContext);
            const workItemIdsSet = dataSource.getWorkItemIdsSet();
            if (filteredIds.length < 1000 && workItemIdsSet.size !== 0) {
                // page in more items
                const backlogWorkItemService = this.context.pageContext.getService<IBacklogWorkItemService>("IBacklogWorkItemService");
                const toPageCount = 1000 - (Object.keys(allRows).length - workItemIdsSet.size);
                backlogWorkItemService.pageMoreItems(toPageCount, this.showLoadingSpinnerOnFilterChange);
            }

            if (workItemIdsSet.size !== 0) {
                // show the filter header
                dataSource.getShowFilterHeader().value = true;
            }
        }

        dataSource.filterWorkItems(filteredIds, flatFilter, this.props.keepHierarchyWithFilters?.value);
    };

    private _onChangeEvent = () => {
        let filterState: IFilterState = this.props.filter.getState();
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        if (Object.keys(filterState).length) {
            this.updateFilterState(filterState, historyService);
        }
        if (!_.isEqual(this.props.filter.getAppliedState(), filterState)) {
            this.props.filter.setState(filterState);
        }

        this._onFilterChanged(mapToFilterState(this.props.filter.getState()), "save-to-server");
    };

    private updateFilterState = (filterState: IFilterState, historyService: IVssHistoryService) => {
        const newHistoryState = mapFilterStateToHistoryState(mapToFilterState(filterState));
        const historyState = historyService.getState();
        for (const key of Object.keys(newHistoryState)) {
            historyState[key] = newHistoryState[key];
        }
        historyService.replaceState(historyState);
    } 
}
